import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { rutaLogin, rutaPostAuth } from '../../utils/rutasAcceso';
import { PASSWORD_MIN } from '../../utils/password';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';


const Register = () => {
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Se abre DENTRO del iframe del micrositio (RematePage navega por SPA, sin
  // salir del embed). El por que y su dependencia con el CSS estan en el hook.
  useAvisarAltoAlPadre();

  // Si la persona llega desde un micrositio, la URL trae ?evento=<slug>. Se lo
  // pasamos al backend para que le deje pedida la autorizacion a esa cabaña.
  const eventoSlug = searchParams.get('evento');

  const onFinish = async (values) => {
    setLoading(true);
    let result;
    try {
      result = await register({
        nombre: values.nombre,
        email: values.email,
        password: values.password,
        aceptaTerminos: values.acepta_terminos,
        eventoSlug,
      });
    } finally {
      setLoading(false);
    }

    if (result?.success) {
      // El alta pública crea SIEMPRE un comprador (rol 3): el backend fuerza
      // `id_rol` y no acepta lo que venga del body. Así que va al panel del
      // comprador, NO a /dashboard, que es el MainLayout del productor y donde
      // todas las llamadas le devolverían 403 o vacío.
      // Mismo criterio de ruteo que Login.jsx: una sola forma, no dos.
      // `bienvenida` lo levanta ClienteLayout para explicarle que todavía no
      // puede ofertar hasta que la cabaña lo habilite.
      navigate(
        rutaPostAuth(result.user, eventoSlug),
        { state: { bienvenida: true } },
      );
      return;
    }

    // Ya tenía cuenta: lo mandamos al login arrastrando el evento del que vino,
    // así al entrar cae en el remate que estaba mirando. No le confirmamos que
    // la cuenta existe ni de qué cabaña es — eso lo decide el backend, que
    // responde igual de genérico siempre.
    if (result?.code === 'email_existente') {
      navigate(rutaLogin(eventoSlug));
    }
  };

  return (
    <div className="auth-container">
      <Card className="auth-card" variant="borderless">
        {/* Wordmark igual que en Login: PUJA en Mont Heavy y la bajada en
            Montserrat. Antes iba "Puja - Gestión de Cabañas" en un solo h1, o
            sea TODO en Mont a 56px, con guión. */}
        <div className="auth-title">
          <h1 className="puja-wordmark">PUJA</h1>
          <p>Gestión de Cabañas</p>
          <span className="auth-subtitle">Crear nueva cuenta</span>
        </div>

        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="nombre"
            rules={[{ required: true, message: 'Ingresá tu nombre' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Nombre completo"
              autoComplete="name"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Ingresá tu email' },
              { type: 'email', message: 'Email inválido' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Email"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Ingresá tu contraseña' },
              { min: PASSWORD_MIN, message: `Mínimo ${PASSWORD_MIN} caracteres` }
            ]}
          >
            {/* `new-password` y no `current-password`: sin esto el gestor de
                claves ofrece la guardada en vez de generar una, y despues
                autocompleta "Confirmar contraseña" con ella — disparando el
                validator contra un valor que la persona nunca tipeo. */}
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Contraseña"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirm"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirmá tu contraseña' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Las contraseñas no coinciden'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirmar contraseña"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="acepta_terminos"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Debés aceptar los términos y condiciones')),
              },
            ]}
          >
            {/* TODO: cuando exista la pagina de terminos, linkear el texto.
                Hoy no hay ruta /terminos y un link roto es peor que sin link. */}
            <Checkbox>Acepto los términos y condiciones</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              Registrarse
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            {/* El slug también viaja por acá: si se pierde en el cross-link, la
                persona vuelve al login sin evento y pierde el remate que venía
                mirando. Mismo criterio que el link espejo en Login.jsx. */}
            ¿Ya tenés cuenta?{' '}
            <Link to={rutaLogin(eventoSlug)}>
              Iniciá sesión
            </Link>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register;
